import type { AreaEventParams, RangeParams, SiteFileKind } from './types'

// Convention: [resource, kind, id?, params?]; invalidate by prefix. Params objects are last so
// prefix matching works.
export const qk = {
  auth: {
    me: ['auth', 'me'] as const,
    avatar: ['auth', 'avatar'] as const,
    mfa: ['auth', 'mfa'] as const,
  },
  companies: {
    all: ['companies'] as const,
    list: () => ['companies', 'list'] as const,
    detail: (id: string) => ['companies', 'detail', id] as const,
  },
  // Outside the 'companies' prefix so invalidating the list doesn't refetch every logo blob.
  // `id` is 'me' for the caller's own company.
  // Same reason: a per-row image blob shouldn't be refetched when the users list is invalidated.
  userAvatar: (id: string, version: string | null) => ['user-avatar', id, version] as const,
  companyLogo: (id: string, version: string | null) => ['company-logo', id, version] as const,
  entra: {
    config: ['entra', 'config'] as const,
  },
  inductSafe: {
    config: ['inductsafe', 'config'] as const,
    siteLinks: ['inductsafe', 'site-links'] as const,
  },
  sites: {
    all: ['sites'] as const,
    list: (p: { active?: boolean }) => ['sites', 'list', p] as const,
    detail: (id: string) => ['sites', 'detail', id] as const,
    liveAll: ['sites', 'live'] as const,
    live: (id: string) => ['sites', 'live', id] as const,
    livePositions: (id: string) => ['sites', 'live-positions', id] as const,
    conditions: (id: string) => ['sites', 'conditions', id] as const,
    historyAll: ['sites', 'history'] as const,
    history: (id: string, p: RangeParams) => ['sites', 'history', id, p] as const,
    files: (id: string, kind: SiteFileKind) => ['sites', 'files', id, kind] as const,
    photo: (id: string, fileId: string) => ['sites', 'photo', id, fileId] as const,
    contacts: (id: string) => ['sites', 'contacts', id] as const,
    notifications: (id: string) => ['sites', 'notifications', id] as const,
    geometryAll: (id: string) => ['sites', 'geometry', id] as const,
    zones: (id: string) => ['sites', 'geometry', id, 'zones'] as const,
    tripwires: (id: string) => ['sites', 'geometry', id, 'tripwires'] as const,
    zoneOccupancy: (id: string) => ['sites', 'zone-occupancy', id] as const,
    zoneEvents: (id: string, p: AreaEventParams) => ['sites', 'zone-events', id, p] as const,
    tripwireEvents: (id: string, p: AreaEventParams) => ['sites', 'tripwire-events', id, p] as const,
  },
  emergencies: {
    all: ['emergencies'] as const,
    active: ['emergencies', 'active'] as const,
    site: (siteId: string) => ['emergencies', 'site', siteId] as const,
  },
  users: {
    all: ['users'] as const,
    listAll: () => ['users', 'list-all'] as const,
    detail: (id: string) => ['users', 'detail', id] as const,
  },
  employees: {
    all: ['employees'] as const,
    list: (p: { active?: boolean }) => ['employees', 'list', p] as const,
    detail: (id: string) => ['employees', 'detail', id] as const,
    sessionsAll: ['employees', 'sessions'] as const,
    sessions: (id: string, p: RangeParams) => ['employees', 'sessions', id, p] as const,
    sessionsRange: (id: string, from: string, to: string) => ['employees', 'sessions', id, 'range', { from, to }] as const,
    zoneEvents: (id: string, p: AreaEventParams) => ['employees', 'zone-events', id, p] as const,
    tripwireEvents: (id: string, p: AreaEventParams) => ['employees', 'tripwire-events', id, p] as const,
    summary: (id: string) => ['employees', 'summary', id] as const,
    files: (id: string) => ['employees', 'files', id] as const,
    emergencyContacts: (id: string) => ['employees', 'emergency-contacts', id] as const,
    locationRequests: (id: string) => ['employees', 'location-requests', id] as const,
    locationShare: (id: string) => ['employees', 'location-share', id] as const,
    audit: (id: string, page: number) => ['employees', 'audit', id, page] as const,
    activity: (id: string, page: number) => ['employees', 'activity', id, page] as const,
    offboarding: (id: string) => ['employees', 'offboarding', id] as const,
    checklists: (id: string) => ['employees', 'checklists', id] as const,
    transfers: (id: string) => ['employees', 'transfers', id] as const,
  },
  pay: {
    all: ['pay'] as const,
    overview: (id: string) => ['pay', 'overview', id] as const,
  },
  performance: {
    all: ['performance'] as const,
    notes: (id: string) => ['performance', 'notes', id] as const,
    disciplinaries: (id: string) => ['performance', 'disciplinaries', id] as const,
    nineBox: (id: string) => ['performance', 'ninebox', id] as const,
    goals: (p: { employeeId?: string; status?: string }) => ['performance', 'goals', p] as const,
    periods: ['performance', 'periods'] as const,
    criteria: ['performance', 'criteria'] as const,
    reviews: (p: { periodId?: string; employeeId?: string }) => ['performance', 'reviews', p] as const,
  },
  assets: {
    all: ['assets'] as const,
    list: (p: { status?: string; category?: string; search?: string; attention?: boolean }) => ['assets', 'list', p] as const,
    maintenance: (id: string) => ['assets', 'maintenance', id] as const,
    distance: (id: string) => ['assets', 'distance', id] as const,
    history: (id: string) => ['assets', 'history', id] as const,
    locations: ['assets', 'locations'] as const,
    locationHistory: (id: string) => ['assets', 'location-history', id] as const,
    forEmployee: (id: string, all: boolean) => ['assets', 'employee', id, all] as const,
  },
  bleTags: {
    all: ['ble-tags'] as const,
    list: () => ['ble-tags', 'list'] as const,
  },
  gateways: {
    all: ['gateways'] as const,
    list: () => ['gateways', 'list'] as const,
  },
  leave: {
    all: ['leave'] as const,
    categories: ['leave', 'categories'] as const,
    /** Prefix of every requests list, for refreshing them all (an attachment changes a request's fileCount). */
    allRequests: ['leave', 'requests'] as const,
    requests: (p: { employeeId?: string; status?: string }) => ['leave', 'requests', p] as const,
    balances: (employeeId: string) => ['leave', 'balances', employeeId] as const,
    files: (requestId: string) => ['leave', 'files', requestId] as const,
  },
  // Outside the 'leave' prefix: a leave decision refreshes every leave list, and shouldn't refetch the company's routing too.
  sickLeaveSettings: ['sick-leave-settings'] as const,
  // Also outside it: recording a closure refreshes every leave query, and mustn't ask the server for a preview of what it just recorded.
  weatherClosurePreview: (p: { siteId: string; fromDate: string; toDate: string }) => ['weather-closure-preview', p] as const,
  timesheets: {
    all: ['timesheets'] as const,
    list: (p: { from: string; to: string; siteId?: string; agencyId?: string }) => ['timesheets', 'list', p] as const,
  },
  checklistTemplates: {
    all: ['checklist-templates'] as const,
  },
  reports: {
    all: ['reports'] as const,
    dailyHeadcount: (p: { siteId?: string; date: string; endDate?: string; agencyId?: string }) => ['reports', 'daily-headcount', p] as const,
  },
  shifts: {
    all: ['shifts'] as const,
    list: (p: { from: string; to: string; siteId?: string; employeeId?: string }) => ['shifts', 'list', p] as const,
    detail: (id: string) => ['shifts', 'detail', id] as const,
    series: (id: string) => ['shifts', 'series', id] as const,
  },
  jobs: {
    all: ['jobs'] as const,
    list: (p: { from: string; to: string; siteId?: string }) => ['jobs', 'list', p] as const,
    taskPhoto: (jobId: string, taskId: string, photoId: string) => ['jobs', 'task-photo', jobId, taskId, photoId] as const,
    pendingClaims: (p: { from: string; to: string; siteId?: string }) => ['jobs', 'pending-claims', p] as const,
    // Under the jobs prefix so every job mutation (assign, unassign, publish, edit) refreshes the cost figures too.
    costs: (p: { from: string; to: string; siteId?: string; includeDrafts?: boolean; agencyId?: string }) => ['jobs', 'costs', p] as const,
    cost: (id: string) => ['jobs', 'cost', id] as const,
  },
  jobTypes: {
    all: ['job-types'] as const,
    list: ['job-types', 'list'] as const,
  },
  agencies: {
    all: ['agencies'] as const,
    list: ['agencies', 'list'] as const,
  },
  reminderProfiles: {
    all: ['reminder-profiles'] as const,
    list: ['reminder-profiles', 'list'] as const,
  },
  roster: {
    all: ['roster'] as const,
    attendanceExceptions: (p: { from: string; to: string }) => ['roster', 'attendance-exceptions', p] as const,
    availability: (p: { from: string; to: string; siteId?: string }) => ['roster', 'availability', p] as const,
  },
  swaps: {
    all: ['swaps'] as const,
    list: (p: { status?: string }) => ['swaps', 'list', p] as const,
    detail: (id: string) => ['swaps', 'detail', id] as const,
  },
  chat: {
    all: ['chat'] as const,
    list: ['chat', 'list'] as const,
    detail: (id: string) => ['chat', 'detail', id] as const,
    messages: (id: string) => ['chat', 'messages', id] as const,
    typing: (id: string) => ['chat', 'typing', id] as const,
    attachment: (id: string, messageId: string) => ['chat', 'attachment', id, messageId] as const,
    groupPhoto: (id: string, version: string | null) => ['chat', 'group-photo', id, version] as const,
    personPhoto: (employeeId: string, version: string | null) => ['chat', 'person-photo', employeeId, version] as const,
  },
  chatReview: {
    all: ['chat-review'] as const,
    list: (f: object) => ['chat-review', 'list', f] as const,
    detail: (id: string) => ['chat-review', 'detail', id] as const,
    messages: (id: string) => ['chat-review', 'messages', id] as const,
    attachment: (id: string, messageId: string) => ['chat-review', 'attachment', id, messageId] as const,
    reports: (status: string | null) => ['chat-review', 'reports', status] as const,
  },
  billing: {
    all: ['billing'] as const,
    mine: ['billing', 'mine'] as const,
    adminOverview: ['billing', 'admin-overview'] as const,
    adminPayments: (companyId: string) => ['billing', 'admin-payments', companyId] as const,
  },
  resellers: {
    all: ['resellers'] as const,
    list: (status?: string) => ['resellers', 'list', status] as const,
    me: ['resellers', 'me'] as const,
    myReferrals: ['resellers', 'me', 'referrals'] as const,
    directory: ['resellers', 'directory'] as const,
  },
}
